import type { CuentaBancaria, CuentaDelPago, Movimiento } from "../api/tipos.ts";
import { haceRato } from "../formato.ts";

/**
 * Cómo se lee a qué cuenta entró un pago (SPEC-030 §6). Con las palabras de
 * quien cobra: «lo dijo el banco», no «origen: banco» (P-1).
 */

/** «Ahorros principal · Bancolombia *1592», o lo que se sepa. */
export function nombreDeCuenta(c: CuentaDelPago | null | undefined): string {
  if (!c?.cuenta) return "";
  const numero = [c.banco, c.cuenta].filter(Boolean).join(" ");
  return c.nombre ? `${c.nombre} · ${numero}` : numero;
}

/** La forma corta, para una columna: el nombre, o el número si no tiene. */
export function cuentaCorta(c: CuentaDelPago | null | undefined): string {
  if (!c?.cuenta) return "";
  return c.nombre || [c.banco, c.cuenta].filter(Boolean).join(" ");
}

/** De dónde salió, en una frase. */
export function deDondeSalio(c: CuentaDelPago | null | undefined): string {
  switch (c?.origen) {
    case "persona":
      return `la eligió ${c.quien || "alguien"}${c.cuando ? ` ${haceRato(c.cuando)}` : ""}`;
    case "banco":
      return c.nombre ? "lo dijo el banco" : "lo dijo el banco; no está en tus cuentas";
    case "deducida":
      return c.porque || "deducida de tus cuentas";
    default:
      return "";
  }
}

const igual = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Cómo se reconoce una cuenta de la lista: «*1592» o la llave. Es lo mismo que
 * manda y devuelve el servidor (`identificador` en `cuentas-bancarias.js`).
 */
export const identificadorDe = (c: Pick<CuentaBancaria, "tipo" | "ultimos" | "llave">) =>
  c.tipo === "Llave" ? (c.llave ?? "").trim() : `*${c.ultimos ?? ""}`;

/** La clave de una cuenta de la lista para un `<select>`: «Bancolombia|*1592», «Nu|@tunegocio». */
export const llaveDe = (c: Pick<CuentaBancaria, "banco" | "tipo" | "ultimos" | "llave">) =>
  `${c.banco}|${identificadorDe(c)}`;

/** Lo que se ve de una cuenta en una lista: «Ahorros principal · Bancolombia *1592». */
export const rotuloDeCuenta = (c: CuentaBancaria) => `${c.nombre} · ${c.banco} ${identificadorDe(c)}`;

/** La cuenta de la lista a la que corresponde lo resuelto, si alguna. */
export function enLaLista(c: CuentaDelPago | null | undefined, lista: CuentaBancaria[]): CuentaBancaria | null {
  if (!c?.cuenta) return null;
  return lista.find((x) => igual(x.banco, c.banco) && igual(identificadorDe(x), c.cuenta)) ?? null;
}

/** Los pagos que nadie sabe a qué cuenta entraron. */
export const sinCuenta = (m: Movimiento) => !m.cuentaBancaria?.cuenta;
